import React, { useState, useRef, useEffect } from 'react';
import { Upload, Edit, Trash, Edit3, Eye, Settings } from 'lucide-react';
import parkingService from '../services/parkingService'
import '../styles/parqueoStyle.css'

const ParkingLotManager = () => {
  const [parkingImage, setParkingImage] = useState(null);
  const [spots, setSpots] = useState([]);
  const [booms, setBooms] = useState([]);
  const [isPlacingSpot, setIsPlacingSpot] = useState(false);
  const [isPlacingBoom, setIsPlacingBoom] = useState(false);
  const [nextSpotId, setNextSpotId] = useState(1);
  const [nextBoom, setNextBoomId] = useState(1);
  const [registeredParkings, setRegisteredParkings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingParking, setEditingParking] = useState(null);

  // Nuevo estado para controlar el modo de vista
  const [viewMode, setViewMode] = useState('edit'); // 'edit' o 'view'

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    parkingName: '',
    capacity: '',
    tarify: '',
    blacklist: [],
    spots: [],
    booms: [],
    image: null
  });

  // Estado de ocupación en tiempo real desde el backend
  const [liveOccupancy, setLiveOccupancy] = useState({ currentOccupancy: 0, capacity: 0 });

  useEffect(() => {
    loadParkings();
  }, []);

  // Auto-refresh cada 10 segundos en modo visualización
  useEffect(() => {
    if (viewMode !== 'view' || !editingParking) return;

    const refreshOccupancy = async () => {
      try {
        const updated = await parkingService.getParkingById(editingParking._id);
        const p = updated.parking || updated;
        setLiveOccupancy({
          currentOccupancy: p.currentOccupancy || 0,
          capacity: p.capacity || 0,
        });
        // Actualizar spots y booms con datos frescos del backend
        if (p.spots) setSpots(p.spots.map(s => ({ ...s, rotation: s.rotation || 0 })));
        if (p.booms) setBooms(p.booms.map(b => ({ ...b, rotation: b.rotation || 0 })));
      } catch (err) {
        console.error('Error actualizando ocupación:', err);
      }
    };

    refreshOccupancy(); // primera carga inmediata
    const interval = setInterval(refreshOccupancy, 10000); // cada 10s
    return () => clearInterval(interval);
  }, [viewMode, editingParking]);

  const loadParkings = async () => {
    try {
      setIsLoading(true);
      const response = await parkingService.getAllParkings();
      setRegisteredParkings(response.parkings || response || []);
    } catch (error) {
      console.error('Error al cargar parqueos:', error);
      alert('Error al cargar los parqueos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);

    try {
      const uploadUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '/upload');
      const res = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      const imageUrl = data.imageUrl;
      setParkingImage(imageUrl);
      setSpots([]);
      setNextSpotId(1);
      setFormData(prev => ({ ...prev, image: imageUrl }));
    }
    catch (err) {
      console.error('Error al subir imagen', err);
    }
  };

  const handleImageClick = (event) => {
    // Solo permitir colocar spots/booms en modo edición
    if (viewMode !== 'edit') return;
    if (!isPlacingSpot && !isPlacingBoom) return;

    const element = imageRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    if (isPlacingSpot) {
      setSpots(prev => [...prev, {
        id: nextSpotId.toString(),
        label: `P${nextSpotId}`,
        status: 'available',
        x, y,
        rotation: 0,
      }]);
      setNextSpotId(prev => prev + 1);
    } else if (isPlacingBoom) {
      setBooms(prev => [...prev, {
        boomId: nextBoom.toString(),
        location: 'Enter',
        status: 'closed',
        x, y,
        rotation: 0,
      }]);
      setNextBoomId(prev => prev + 1);
    }
  };

  const deleteSpot = (spotId) => {
    setSpots(prev => prev.filter(spot => spot.id !== spotId));
  };

  const deleteBoom = (boomId) => {
    setBooms(prev => prev.filter(boom => boom.boomId !== boomId));
  };

  const rotateSpot = (spotId) => {
    setSpots(prev => prev.map(spot =>
      spot.id === spotId ? { ...spot, rotation: ((spot.rotation || 0) + 45) % 360 } : spot
    ));
  };

  const rotateBoom = (boomId) => {
    setBooms(prev => prev.map(boom =>
      boom.boomId === boomId ? { ...boom, rotation: ((boom.rotation || 0) + 45) % 360 } : boom
    ));
  };

  const toggleSpotStatus = (spotId) => {
    setSpots(spots.map(spot => {
      if (spot.id === spotId) {
        const statusCycle = {
          'available': 'occupied',
          'occupied': 'reserved',
          'reserved': 'available'
        };
        return { ...spot, status: statusCycle[spot.status] };
      }
      return spot;
    }));
  };

  const [barrierLoading, setBarrierLoading] = useState(null); // boomId que esta cargando

  const toggleBoomStatus = async (boomId) => {
    if (!editingParking?._id) {
      alert('No hay parqueo seleccionado');
      return;
    }
    if (barrierLoading) return; // evitar doble click

    const boom = booms.find(b => b.boomId === boomId);
    if (!boom) return;

    const action = boom.status === 'open' ? 'close' : 'open';
    setBarrierLoading(boomId);

    try {
      if (action === 'open') {
        await parkingService.openBarrier(editingParking._id, boomId);
      } else {
        await parkingService.closeBarrier(editingParking._id, boomId);
      }

      // Actualizar estado visual
      setBooms(booms.map(b => {
        if (b.boomId === boomId) {
          return { ...b, status: action === 'open' ? 'open' : 'closed' };
        }
        return b;
      }));

      // Si se abrio, auto-cerrar visualmente a los 5 seg
      if (action === 'open') {
        setTimeout(() => {
          setBooms(prev => prev.map(b => {
            if (b.boomId === boomId) {
              return { ...b, status: 'closed' };
            }
            return b;
          }));
        }, 5500);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      alert(`Error al ${action === 'open' ? 'abrir' : 'cerrar'} aguja: ${msg}`);
    } finally {
      setBarrierLoading(null);
    }
  };

  const getSpotClass = (spot) => {
    const baseClass = 'parking-spot';
    if (viewMode === 'view') {
      // En modo visualización, mostrar el estado real
      return `${baseClass} parking-spot-${spot.status}`;
    }
    // En modo edición, siempre mostrar como disponible
    return `${baseClass} parking-spot-available`;
  };

  const resetForm = () => {
    setFormData({
      parkingName: '',
      capacity: '',
      tarify: '',
      blacklist: [],
      spots: [],
      booms: [],
      image: null
    });
    setParkingImage(null);
    setSpots([]);
    setBooms([]);
    setNextBoomId(1);
    setNextSpotId(1);
    setIsPlacingSpot(false);
    setIsPlacingBoom(false);
    setEditingParking(null);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      if (!formData.parkingName || !formData.capacity || !formData.tarify) {
        alert('Por favor, completa todos los campos requeridos');
        return;
      }

      const parkingData = {
        parkingName: formData.parkingName,
        capacity: parseInt(formData.capacity),
        tarify: parseFloat(formData.tarify),
        blacklist: formData.blacklist,
        spots: spots,
        booms: booms,
        image: formData.image
      };

      if (editingParking) {
        await parkingService.updateParking(editingParking._id, parkingData);
        alert('Parqueo actualizado exitosamente');
      } else {
        await parkingService.createParking(parkingData);
        alert('Parqueo creado exitosamente');
      }

      resetForm();
      await loadParkings();
    } catch (error) {
      console.error('Error al guardar parqueo:', error);
      alert('Error al guardar el parqueo: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  /*   const handleEdit = (parking) => {
      setEditingParking(parking);
      setFormData({
        parkingName: parking.parkingName || '',
        capacity: parking.capacity || '',
        tarify: parking.tarify || '',
        blacklist: parking.blacklist || [],
        spots: parking.spots || [],
        booms: parking.booms || [],
        image: parking.image || null
      });
  
      if (parking.image) {
        setParkingImage(parking.image);
      }
  
      if (parking.spots && parking.spots.length > 0) {
        setSpots(parking.spots);
        setNextSpotId(Math.max(...parking.spots.map(s => parseInt(s.id))) + 1);
      }
      
      if (parking.booms && parking.booms.length > 0) {
        setBooms(parking.booms);
        setNextBoomId(Math.max(...parking.booms.map(b => parseInt(b.boomId))) + 1);
      }
    }; */

  const handleEdit = (parking) => {
    setEditingParking(parking);
    setLiveOccupancy({
      currentOccupancy: parking.currentOccupancy || 0,
      capacity: parking.capacity || 0,
    });
    setFormData({
      parkingName: parking.parkingName || '',
      capacity: parking.capacity || '',
      tarify: parking.tarify || '',
      blacklist: parking.blacklist || [],
      spots: parking.spots || [],
      booms: parking.booms || [],
      image: parking.image || null
    });

    if (parking.image) {
      setParkingImage(parking.image);
    }

    if (parking.spots && parking.spots.length > 0) {
      // Asegurar que cada spot tenga la propiedad rotation
      const spotsWithRotation = parking.spots.map(spot => ({
        ...spot,
        rotation: spot.rotation || 0 // Si no tiene rotation, usar 0
      }));
      setSpots(spotsWithRotation);

      // Calcular el siguiente ID basado en los spots existentes
      const maxSpotId = Math.max(...parking.spots.map(s => {
        const id = parseInt(s.id) || parseInt(s.label?.replace('P', '')) || 0;
        return id;
      }));
      setNextSpotId(maxSpotId + 1);
    }

    if (parking.booms && parking.booms.length > 0) {
      // Asegurar que cada boom tenga la propiedad rotation
      const boomsWithRotation = parking.booms.map(boom => ({
        ...boom,
        rotation: boom.rotation || 0 // Si no tiene rotation, usar 0
      }));
      setBooms(boomsWithRotation);

      // Calcular el siguiente ID basado en los booms existentes
      const maxBoomId = Math.max(...parking.booms.map(b => {
        const id = parseInt(b.boomId) || parseInt(b.boomId?.replace('A', '')) || 0;
        return id;
      }));
      setNextBoomId(maxBoomId + 1);
    }
  };

  const handleDelete = async (parkingId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este parqueo?')) {
      try {
        setIsLoading(true);
        await parkingService.deleteParking(parkingId);
        alert('Parqueo eliminado exitosamente');
        await loadParkings();
      } catch (error) {
        console.error('Error al eliminar parqueo:', error);
        alert('Error al eliminar el parqueo: ' + (error.response?.data?.message || error.message));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const cancelEdit = () => {
    resetForm();
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    // Cancelar modo de colocación al cambiar de vista
    setIsPlacingSpot(false);
    setIsPlacingBoom(false);
  };

  return (
    <div className="parking-container">
      <h1 className="parking-header-title">Gestión de Parqueos</h1>

      {/* Switch de modo de vista */}
      <div className="parking-view-mode-switch">
        <button
          className={`parking-mode-button ${viewMode === 'edit' ? 'active' : ''}`}
          onClick={() => handleViewModeChange('edit')}
        >
          <Settings size={18} />
          Modo Edición
        </button>
        <button
          className={`parking-mode-button ${viewMode === 'view' ? 'active' : ''}`}
          onClick={() => handleViewModeChange('view')}
        >
          <Eye size={18} />
          Modo Visualización
        </button>
      </div>

      <div className="parking-max-width-container">
        <div className="parking-main-grid">
          {/* Parking Map */}
          <div className="parking-map-card">
            <h2 className="parking-map-title">
              {viewMode === 'edit' ? 'Edición de Layout' : 'Vista de Operación'}
            </h2>

            {parkingImage ? (
              <div
                ref={containerRef}
                className="parking-map-container"
                onClick={handleImageClick}
                style={{
                  cursor: (viewMode === 'edit' && (isPlacingSpot || isPlacingBoom)) ? 'crosshair' : 'default',
                  position: 'relative'
                }}
              >
                <img
                  ref={imageRef}
                  src={parkingImage}
                  alt="Parking lot"
                  className="parking-image"
                />

                {/* Parking Spots Overlay */}
                {spots.map(spot => (
                  <div
                    key={spot.id}
                    className={getSpotClass(spot)}
                    style={{
                      left: `${spot.x}%`,
                      top: `${spot.y}%`,
                      transform: `translate(-50%, -50%) rotate(${spot.rotation || 0}deg)`
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (viewMode === 'view') {
                        toggleSpotStatus(spot.id);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (viewMode === 'edit') {
                        rotateSpot(spot.id);
                      }
                    }}
                  >
                    {spot.label}

                    {viewMode === 'edit' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSpot(spot.id);
                        }}
                        className="parking-delete-spot-button"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}

                {/* Booms/Agujas Overlay */}
                {booms.map(boom => (
                  <div
                    key={boom.boomId}
                    className={`parking-spot parking-spot-needle ${viewMode === 'view' && boom.status === 'open' ? 'boom-open' : ''}`}
                    style={{
                      left: `${boom.x}%`,
                      top: `${boom.y}%`,
                      transform: `translate(-50%, -50%) rotate(${boom.rotation || 0}deg)`
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (viewMode === 'view') {
                        toggleBoomStatus(boom.boomId);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (viewMode === 'edit') {
                        rotateBoom(boom.boomId);
                      }
                    }}
                  >
                    A{boom.boomId}
                    {viewMode === 'view' && (
                      <span className="boom-status-indicator">
                        {barrierLoading === boom.boomId ? '⏳' : boom.status === 'open' ? '🟢' : '🔴'}
                      </span>
                    )}

                    {viewMode === 'edit' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBoom(boom.boomId);
                        }}
                        className="parking-delete-spot-button"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="parking-upload-placeholder">
                <Upload size={48} style={{ margin: '0 auto 1rem', color: '#9ca3af' }} />
                <p style={{ color: '#6b7280' }}>Carga imagen del parqueo para empezar</p>
              </div>
            )}

            {/* Controles según el modo */}
            {viewMode === 'edit' ? (
              <div className="parking-button-container">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  ref={fileInputRef}
                  className="parking-hidden-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="parking-upload-button"
                >
                  <Upload size={16} />
                  Cargar Mapa
                </button>

                <button
                  onClick={() => { setIsPlacingSpot(!isPlacingSpot); setIsPlacingBoom(false); }}
                  className={`parking-place-spot-button ${isPlacingSpot ? 'parking-placing' : 'parking-upload-button'}`}
                >
                  <Edit3 size={16} />
                  {isPlacingSpot ? 'Cancelar' : 'Nuevo campo'}
                </button>

                <button
                  onClick={() => { setIsPlacingBoom(!isPlacingBoom); setIsPlacingSpot(false); }}
                  className={`parking-place-spot-button ${isPlacingBoom ? 'parking-placing' : 'parking-not-placing'}`}
                >
                  {isPlacingBoom ? 'Cancelar' : 'Agujas'}
                </button>
              </div>
            ) : (
              <div className="parking-view-info">
                {/* Barra de ocupación en tiempo real */}
                <div className="parking-occupancy-bar-container">
                  <div className="parking-occupancy-header">
                    <span className="parking-occupancy-title">Ocupacion en Tiempo Real</span>
                    <span className="parking-occupancy-count">
                      {liveOccupancy.currentOccupancy} / {liveOccupancy.capacity}
                    </span>
                  </div>
                  <div className="parking-occupancy-bar">
                    <div
                      className={`parking-occupancy-fill ${
                        liveOccupancy.capacity > 0 && (liveOccupancy.currentOccupancy / liveOccupancy.capacity) >= 0.9
                          ? 'critical'
                          : liveOccupancy.capacity > 0 && (liveOccupancy.currentOccupancy / liveOccupancy.capacity) >= 0.7
                          ? 'warning'
                          : ''
                      }`}
                      style={{
                        width: liveOccupancy.capacity > 0
                          ? `${Math.min((liveOccupancy.currentOccupancy / liveOccupancy.capacity) * 100, 100)}%`
                          : '0%'
                      }}
                    />
                  </div>
                  <div className="parking-occupancy-percentage">
                    {liveOccupancy.capacity > 0
                      ? `${((liveOccupancy.currentOccupancy / liveOccupancy.capacity) * 100).toFixed(1)}% ocupado`
                      : 'Sin capacidad definida'}
                    {' — '}
                    <span style={{ color: '#4ade80' }}>
                      {Math.max(liveOccupancy.capacity - liveOccupancy.currentOccupancy, 0)} disponibles
                    </span>
                  </div>
                </div>

                <div className="parking-stats">
                  <div className="stat-item">
                    <span className="stat-label">Capacidad:</span>
                    <span className="stat-value">{liveOccupancy.capacity}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Ocupados (LPR):</span>
                    <span className="stat-value occupied">{liveOccupancy.currentOccupancy}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Disponibles:</span>
                    <span className="stat-value available">{Math.max(liveOccupancy.capacity - liveOccupancy.currentOccupancy, 0)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Spots Marcados:</span>
                    <span className="stat-value">{spots.filter(s => s.status === 'occupied').length} / {spots.length}</span>
                  </div>
                </div>
                <p className="parking-view-help">
                  Se actualiza cada 10 segundos. Click en las agujas para abrirlas/cerrarlas.
                </p>
              </div>
            )}
          </div>

          {/* Control Panel */}
          <div className="parking-nav">
            {viewMode === 'edit' && (
              <div className="parking-sidebar-card">
                <h2>{editingParking ? 'Editar Parqueo' : 'Registrar Parqueo'}</h2>

                <div className='parking-col-span-1'>
                  <div className="parking-form-group">
                    <label>Nombre del Parqueo</label>
                    <input
                      type="text"
                      name="parkingName"
                      value={formData.parkingName}
                      onChange={handleChange}
                      className="parking-form-control"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="parking-form-group">
                    <label>Capacidad</label>
                    <input
                      type="number"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleChange}
                      className="parking-form-control"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="parking-form-group">
                    <label>Tarifa</label>
                    <input
                      type="number"
                      step="0.01"
                      name="tarify"
                      value={formData.tarify}
                      onChange={handleChange}
                      className="parking-form-control"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="parking-form-group">
                    <label>Blacklist (separar por comas)</label>
                    <input
                      type="text"
                      name="blacklist"
                      value={Array.isArray(formData.blacklist) ? formData.blacklist.join(', ') : formData.blacklist}
                      onChange={handleChange}
                      className="parking-form-control"
                      placeholder="ABC123, DEF456, GHI789"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="parking-upload-button"
                    onClick={handleSave}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Guardando...' : (editingParking ? 'Actualizar' : 'Guardar')}
                  </button>

                  {editingParking && (
                    <button
                      className="parking-upload-button"
                      onClick={cancelEdit}
                      disabled={isLoading}
                      style={{ backgroundColor: '#6b7280' }}
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Manager list */}
            <div className="parking-sidebar-card">
              <h2>Parqueos Registrados</h2>
              {isLoading && <p>Cargando...</p>}
              <div className="parking-table-wrapper">
                <table className="parking-table">
                  <thead className="parking-table-header">
                    <tr>
                      <th>Nombre</th>
                      <th>Capacidad</th>
                      <th>Imagen</th>
                      <th>Tarifa</th>
                      <th className="parking-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registeredParkings.map((parking, index) => (
                      <tr key={parking._id} className={index % 2 === 0 ? 'parking-table-row-even' : 'parking-table-row-odd'}>
                        <td className="parking-table-cell">{parking.parkingName}</td>
                        <td className="parking-table-cell">{parking.capacity}</td>
                        <td className="parking-table-cell parking-muted">
                          {parking.image ? 'Sí' : 'No'}
                        </td>
                        <td className="parking-table-cell">₡{parking.tarify}</td>
                        <td className="parking-table-cell parking-center">
                          <div className="parking-action-buttons">
                            <button
                              onClick={() => handleEdit(parking)}
                              className="parking-action-icon"
                              title="Editar"
                              disabled={isLoading}
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(parking._id)}
                              className="parking-action-icon parking-delete-icon"
                              title="Eliminar"
                              disabled={isLoading}
                            >
                              <Trash size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {registeredParkings.length === 0 && !isLoading && (
                  <p style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    No hay parqueos registrados
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParkingLotManager;